import { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUGGESTIONS = [
  "What documents do I need for GST registration?",
  "How do I book a free service?",
  "Tell me about the Saturday digital camp",
];

const getSessionId = () => {
  let id = localStorage.getItem("nuc-assistant-session");
  if (!id) {
    id = `sess_${crypto.randomUUID()}`;
    localStorage.setItem("nuc-assistant-session", id);
  }
  return id;
};

export default function AssistantWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(getSessionId());
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    fetch(`${API}/assistant/history/${sessionId.current}`)
      .then((r) => (r.ok ? r.json() : []))
      .then((rows) =>
        setMessages(
          rows.map((m) => ({ role: m.role, content: m.content }))
        )
      )
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages, open]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    const setLast = (fn) =>
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { ...copy[copy.length - 1], content: fn(copy[copy.length - 1].content) };
        return copy;
      });
    try {
      const res = await fetch(`${API}/assistant/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message }),
      });
      if (!res.ok || !res.body) {
        const detail = await res.json().catch(() => null);
        throw new Error(detail?.detail || "The assistant is unavailable right now.");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5));
            if (payload.delta) setLast((c) => c + payload.delta);
            if (payload.error) setLast(() => payload.error);
          } catch {
            // ignore malformed chunk
          }
        }
      }
      setLast((c) => c || "I could not answer that. Please try rephrasing.");
    } catch (err) {
      setLast(() => err.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="assistant-toggle-button"
        aria-label={open ? "Close help assistant" : "Open help assistant"}
        className="fixed bottom-5 right-5 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-blue-900 text-white shadow-lg transition-colors duration-200 hover:bg-blue-800"
      >
        {open ? <X className="h-6 w-6" aria-hidden="true" /> : <MessageCircle className="h-6 w-6" aria-hidden="true" />}
      </button>

      {open && (
        <div
          data-testid="assistant-panel"
          className="fixed bottom-24 right-4 z-40 flex h-[70vh] max-h-[520px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl sm:right-5"
        >
          <div className="border-b border-slate-200 bg-blue-900 px-5 py-4">
            <p className="font-display text-base font-bold tracking-tight text-white">
              NUC Help Assistant
            </p>
            <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-blue-200">
              Services · Documents · Booking · Powered by Claude AI
            </p>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto px-4 py-4" data-testid="assistant-messages">
            {messages.length === 0 && (
              <div>
                <p className="rounded-lg bg-slate-50 px-4 py-3 text-sm leading-relaxed text-slate-700">
                  Namaste! I can help with NUC Service's 13 service categories — eligibility,
                  documents, fees, booking and notices. What do you need help with?
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button
                      key={s}
                      onClick={() => send(s)}
                      data-testid={`assistant-suggestion-${i}`}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-left text-xs font-medium text-slate-700 transition-colors duration-200 hover:border-blue-900 hover:text-blue-900"
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`assistant-message-${i}`}
                className={`whitespace-pre-wrap rounded-lg px-4 py-3 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-8 bg-blue-900 text-white"
                    : "mr-8 bg-slate-50 text-slate-700"
                }`}
              >
                {m.content}
                {m.role === "assistant" && busy && i === messages.length - 1 && !m.content && (
                  <span className="inline-block animate-pulse text-slate-400">Thinking…</span>
                )}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2 border-t border-slate-200 p-3">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              data-testid="assistant-input"
              placeholder="Ask about services, documents, fees…"
              className="flex-1 rounded-md border border-slate-300 px-3 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:border-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-900/20"
              maxLength={1000}
            />
            <button
              onClick={() => send()}
              disabled={busy || !input.trim()}
              data-testid="assistant-send-button"
              aria-label="Send message"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-blue-900 text-white transition-colors duration-200 hover:bg-blue-800 disabled:opacity-40"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
